import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, MapPin, DollarSign, Star, Search, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { StageEmptyState } from '../components/StageEmptyState';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



export default function StudiosPage() {
  const [studios, setStudios] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ neighborhood: '', search: '' });

  useEffect(() => {
    fetchNeighborhoods();
    fetchStudios();
  }, []);

  useEffect(() => {
    fetchStudios();
  }, [filters.neighborhood]);

  const fetchNeighborhoods = async () => {
    try {
      const response = await fetch(`${API}/reference/neighborhoods`);
      if (response.ok) setNeighborhoods((await response.json()).neighborhoods);
    } catch (e) {
      console.error('Failed to fetch neighborhoods:', e);
    }
  };

  const fetchStudios = async () => {
    setLoading(true);
    try {
      let url = `${API}/studios?limit=50`;
      if (filters.neighborhood && filters.neighborhood !== 'all') url += `&neighborhood=${encodeURIComponent(filters.neighborhood)}`;
      
      const response = await fetch(url);
      if (response.ok) setStudios(await response.json());
    } catch (error) {
      console.error('Failed to fetch studios:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStudios = studios.filter(studio =>
    !filters.search || studio.name.toLowerCase().includes(filters.search.toLowerCase())
  );

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="studios-page">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-2">Studios</h1>
          <p className="text-muted-foreground">Find recording studios across the DMV for your next session</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search studios..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 bg-muted/50 border-border"
              data-testid="search-studios-input"
            />
          </div>
          <Select value={filters.neighborhood} onValueChange={(v) => setFilters({ ...filters, neighborhood: v })}>
            <SelectTrigger className="w-full sm:w-48 bg-muted/50 border-border" data-testid="studio-hood-filter">
              <SelectValue placeholder="Neighborhood" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Neighborhoods</SelectItem>
              {neighborhoods.map((hood) => (
                <SelectItem key={hood} value={hood}>{hood}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Studios Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredStudios.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="studios-grid">
            {filteredStudios.map((studio, index) => (
              <motion.div
                key={studio.studio_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="glass-card border-border overflow-hidden hover-lift h-full">
                  {/* Photos */}
                  <div className="aspect-video bg-muted relative">
                    {studio.photos?.[0] ? (
                      <img src={studio.photos[0]} alt={studio.name} className="w-full h-full object-cover" />
                    ) : (
                      <div 
                        className="w-full h-full bg-cover bg-center"
                        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1613870948964-7125fa3e1aab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxyZWNvcmRpbmclMjBzdHVkaW8lMjBtaWNyb3Bob25lJTIwbmVvbnxlbnwwfHx8fDE3Njg5ODY3NDB8MA&ixlib=rb-4.1.0&q=85')` }}
                      />
                    )}
                    {studio.is_featured && (
                      <Badge className="absolute top-3 left-3 bg-accent text-background">Featured</Badge>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-heading text-xl font-bold uppercase mb-2">{studio.name}</h3>
                    
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <MapPin className="w-4 h-4" />
                      {studio.neighborhood}
                    </div>

                    {studio.rating > 0 && (
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < studio.rating ? 'text-accent fill-accent' : 'text-muted'}`} />
                        ))}
                        <span className="text-sm text-muted-foreground ml-1">({studio.reviews_count})</span>
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-sm mb-4">
                      <span className="flex items-center gap-1 text-secondary">
                        <DollarSign className="w-4 h-4" />
                        ${studio.hourly_rate}/hr
                      </span>
                    </div>

                    {studio.gear?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {studio.gear.slice(0, 3).map((item) => (
                          <Badge key={item} variant="outline" className="text-xs">{item}</Badge>
                        ))}
                        {studio.gear.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{studio.gear.length - 3}</Badge>
                        )}
                      </div>
                    )}

                    {studio.booking_link && (
                      <a href={studio.booking_link} target="_blank" rel="noopener noreferrer" className="block">
                        <Badge className="bg-primary hover:bg-primary/90 w-full justify-center py-2">
                          Book Now <ExternalLink className="w-3 h-3 ml-1" />
                        </Badge>
                      </a>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <StageEmptyState variant="studios" />
        )}
      </div>
    </div>
  );
}
